/**
 * LongPostChunkManager Component
 * 
 * Advanced chunk management interface for long posts featuring:
 * - Post metadata display with beautiful header design
 * - Paginated chunk loading for better performance
 * - Drag-and-drop reordering functionality
 * - Individual chunk editing and review capabilities
 * - Chunk deletion with proper cleanup
 * - Integration with translation system
 * - Responsive design with floating action buttons
 * - Real-time progress tracking and status updates
 * 
 * Used in: App.js route /long-posts/:id/chunks
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    List,
    Alert,
    Chip,
    Menu,
    MenuItem,
    Fab,
    Paper,
    Grid,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    Add,
    Delete,
    ArrowBack,
    Build
} from '@mui/icons-material';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { userRequest } from '../../requestMethods';
import PageContainer from '../layout/PageContainer';
import LongPostChunkCard from './LongPostChunkCard';
import LongPostChunkEditor from './LongPostChunkEditor';
import TranslationMonitoringDialog from '../shared/TranslationMonitoringDialog';

const LongPostChunkManager = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [post, setPost] = useState(null);
    const [chunks, setChunks] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
    
    // Chunk editing states
    const [chunkDialog, setChunkDialog] = useState({ 
        open: false, 
        chunk: null, 
        isEdit: false, 
        isReview: false 
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuChunkId, setMenuChunkId] = useState(null);
    const [selectedChunk, setSelectedChunk] = useState(null);
    
    // Translation monitoring
    const [monitoringDialog, setMonitoringDialog] = useState({ open: false, loading: false, data: null, chunk: null });

    useEffect(() => {
        if (id) {
            fetchPostAndInitialChunks();
        }
    }, [id]);

    const fetchPostAndInitialChunks = async () => {
        setLoading(true);
        try {
            const [postResponse, chunksResponse] = await Promise.all([
                userRequest.get(`/contentLongPost/${id}`),
                userRequest.get(`/contentLongPost/${id}/chunks`, {
                    params: { start: 0, limit: 30 }
                })
            ]);
            
            setPost(postResponse.data);
            setChunks(chunksResponse.data.chunks);
            setHasMore(chunksResponse.data.hasMore);
        } catch (error) {
            showAlert('Error loading post: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const showAlert = (message, severity = 'info') => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 5000);
    };

    const loadMoreChunks = async () => {
        setLoadingMore(true);
        try {
            const response = await userRequest.get(`/contentLongPost/${id}/chunks`, {
                params: { start: chunks.length, limit: 30 }
            });
            
            setChunks(prev => [...prev, ...response.data.chunks]);
            setHasMore(response.data.hasMore);
            showAlert('Loaded more chunks successfully', 'success');
        } catch (error) {
            showAlert('Error loading more chunks: ' + error.message, 'error');
        } finally {
            setLoadingMore(false);
        }
    };

    const refetchData = async () => {
        try {
            await fetchPostAndInitialChunks();
        } catch (error) {
            showAlert('Error refreshing data: ' + error.message, 'error');
        }
    };

    const cleanupChunkOrders = async () => {
        if (!window.confirm('This will renumber all chunks sequentially (1, 2, 3, ...). Continue?')) {
            return;
        }

        try {
            await userRequest.patch(`/contentLongPost/${id}/chunks/reorder`, { cleanupOrders: true });
            showAlert('Chunk orders cleaned up successfully', 'success');
            refetchData();
        } catch (error) {
            showAlert('Error cleaning up orders: ' + error.message, 'error');
        }
    };

    const handleReviewChunk = (chunk) => {
        setChunkDialog({ 
            open: true, 
            chunk, 
            isEdit: false, 
            isReview: true 
        });
    };

    const handleEditChunk = async (chunk) => {
        setChunkDialog({ 
            open: true, 
            chunk, 
            isEdit: true, 
            isReview: false 
        });
    };

    const handleTranslateChunk = (chunk) => {
        if (chunk.text?._id) {
            navigate(`/text/translate/${chunk.text._id}`);
        } else {
            showAlert('Cannot open translation editor: Text ID not found', 'error');
        }
    };

    const handleMenuOpen = (event, chunk) => {
        setAnchorEl(event.currentTarget);
        setMenuChunkId(chunk._id);
        setSelectedChunk(chunk);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuChunkId(null);
        setSelectedChunk(null);
    };

    const handleDeleteChunk = async () => {
        if (menuChunkId && selectedChunk) {
            const confirmed = window.confirm(
                `Are you sure you want to delete "${selectedChunk.title}"? This will also delete the associated text and translations.`
            );
            if (confirmed) {
                try {
                    await userRequest.delete(`/contentLongPost/${id}/chunks/${menuChunkId}`);
                    showAlert('Chunk deleted successfully', 'success');
                    refetchData();
                } catch (error) {
                    showAlert('Error deleting chunk: ' + error.message, 'error');
                }
            }
            handleMenuClose();
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;
        
        if (sourceIndex === destinationIndex) return;

        const reorderedChunks = Array.from(chunks);
        const [movedChunk] = reorderedChunks.splice(sourceIndex, 1);
        reorderedChunks.splice(destinationIndex, 0, movedChunk);

        // Calculate the new order for the moved chunk based on its new position
        let newOrder;
        if (destinationIndex === 0) {
            // Moving to first position - set order to be before the first chunk
            const firstChunk = reorderedChunks[1]; // The chunk that will be after the moved chunk
            newOrder = firstChunk ? firstChunk.order - 1 : 1;
            if (newOrder <= 0) newOrder = 0.5; // Ensure positive order
        } else if (destinationIndex === reorderedChunks.length - 1) {
            // Moving to last position - set order to be after the last chunk
            const lastChunk = reorderedChunks[destinationIndex - 1]; // The chunk that will be before the moved chunk
            newOrder = lastChunk ? lastChunk.order + 1 : 1000;
        } else {
            // Moving to middle - set order to be between the chunks around it
            const prevChunk = reorderedChunks[destinationIndex - 1];
            const nextChunk = reorderedChunks[destinationIndex + 1];
            const prevOrder = prevChunk ? prevChunk.order : 0;
            const nextOrder = nextChunk ? nextChunk.order : 1000;
            
            newOrder = (prevOrder + nextOrder) / 2;
            
            // If there's no meaningful space between orders, we need to reorder more chunks
            if (Math.abs(newOrder - prevOrder) < 0.1 || Math.abs(nextOrder - newOrder) < 0.1) {
                // Fallback: use a more complex reordering strategy
                showAlert('Complex reordering needed - refreshing data...', 'info');
                refetchData();
                return;
            }
        }

        // Update the moved chunk's order in local state
        const updatedChunks = [...chunks];
        const chunkToUpdate = updatedChunks[sourceIndex];
        chunkToUpdate.order = newOrder;
        
        // Remove from source and insert at destination
        updatedChunks.splice(sourceIndex, 1);
        updatedChunks.splice(destinationIndex, 0, chunkToUpdate);
        
        setChunks(updatedChunks);

        try {
            // Send only the moved chunk's new order
            await userRequest.patch(`/contentLongPost/${id}/chunks/reorder`, { 
                chunkOrders: [{ chunkId: movedChunk._id, newOrder }] 
            });
            showAlert('Chunk reordered successfully', 'success');
        } catch (error) {
            showAlert('Error reordering chunk: ' + error.message, 'error');
            refetchData();
        }
    };

    const handleCloseChunkDialog = () => {
        setChunkDialog({ 
            open: false, 
            chunk: null, 
            isEdit: false, 
            isReview: false 
        });
    };

    const handleCheckTranslations = async (chunk) => {
        if (!chunk?.text?._id) return;

        setMonitoringDialog({ open: true, loading: true, data: null, chunk });

        try {
            const response = await userRequest.get(`/text/check-translations/${chunk.text._id}`);
            setMonitoringDialog({ open: true, loading: false, data: response.data, chunk });
        } catch (error) {
            console.error('Error checking translations:', error);
            
            // Handle 404 (no translation found) specifically
            if (error.response?.status === 404) {
                setMonitoringDialog({ 
                    open: true, 
                    loading: false, 
                    data: { 
                        error: 'No translation found for this chunk. You need to create a translation first.',
                        hasUntranslated: false,
                        untranslatedWords: [],
                        untranslatedPhrases: [],
                        noTranslation: true
                    },
                    chunk
                });
            } else {
                setMonitoringDialog({ 
                    open: true, 
                    loading: false, 
                    data: { 
                        error: 'Failed to check translations. Please try again.',
                        hasUntranslated: false,
                        untranslatedWords: [],
                        untranslatedPhrases: []
                    },
                    chunk
                });
            }
        }
    };

    const handleCloseMonitoringDialog = () => {
        setMonitoringDialog({ open: false, loading: false, data: null, chunk: null });
    };

    const handleFixInEditor = () => {
        if (monitoringDialog.chunk) {
            handleTranslateChunk(monitoringDialog.chunk);
        }
    };

    const handleReadyToServeToggle = async (event) => {
        const newValue = event.target.checked;
        
        try {
            await userRequest.patch(`/contentLongPost/${id}`, {
                isReadyToServe: newValue
            });
            
            setPost(prevPost => ({
                ...prevPost,
                isReadyToServe: newValue
            }));
            
            showAlert(
                newValue ? 'Long post published successfully' : 'Long post unpublished successfully', 
                'success'
            );
        } catch (error) {
            showAlert('Error updating publish status: ' + error.message, 'error');
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <Typography variant="h4">Loading...</Typography>
            </PageContainer>
        );
    }

    if (!post) {
        return (
            <PageContainer>
                <Alert severity="error">Post not found</Alert>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/long-posts')}
                        sx={{ mr: 2 }}
                    >
                        Back to Long Posts
                    </Button>
                </Box>

                {/* Long Post Header */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
                                {post.title}
                            </Typography>
                            
                            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                                <Chip 
                                    label={post.isReadyToServe ? "Ready" : "Draft"} 
                                    color={post.isReadyToServe ? "success" : "warning"}
                                    variant="outlined"
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {post.language?.name}
                                </Typography>
                                {post.author && (
                                    <Typography variant="body2" color="text.secondary">
                                        by {post.author}
                                    </Typography>
                                )}
                            </Box>

                            {post.summary && (
                                <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                                    {post.summary}
                                </Typography>
                            )}

                            <Box display="flex" alignItems="center" gap={2}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="body2" color="text.secondary">
                                        {chunks.length} chunks
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="body2" color="text.secondary">
                                        Category: {post.category}
                                    </Typography>
                                </Box>
                                {post.estimatedReadingTime > 0 && (
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" color="text.secondary">
                                            {post.estimatedReadingTime}m read
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {post.tags && post.tags.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {post.tags.map((tag, index) => (
                                            <Chip 
                                                key={index} 
                                                label={tag} 
                                                size="small" 
                                                variant="outlined"
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={2}>
                                {/* Publish/Unpublish Toggle */}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={post.isReadyToServe || false}
                                            onChange={handleReadyToServeToggle}
                                            color="success"
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2">
                                                {post.isReadyToServe ? 'Published' : 'Draft'}
                                            </Typography>
                                        </Box>
                                    }
                                    labelPlacement="start"
                                    sx={{ 
                                        margin: 0,
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.875rem',
                                            fontWeight: 500
                                        }
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {alert.show && (
                    <Alert severity={alert.severity} sx={{ mb: 2 }}>
                        {alert.message}
                    </Alert>
                )}
            </Box>

            {/* Toolbar */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box />
                <Button
                    variant="outlined"
                    size="small"
                    onClick={cleanupChunkOrders}
                    startIcon={<Build />}
                    sx={{ borderRadius: 2 }}
                >
                    Clean Up Orders
                </Button>
            </Box>

            {/* Chunks Management */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Chunks ({chunks.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Manage individual sections of your long post. You can add, edit, reorder, and translate each chunk.
                </Typography>
            </Box>

            {chunks.length > 0 ? (
                <Box>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="chunks">
                            {(provided) => (
                                <List {...provided.droppableProps} ref={provided.innerRef}>
                                    {chunks.map((chunk, index) => (
                                        <LongPostChunkCard
                                            key={chunk._id}
                                            chunk={chunk}
                                            index={index}
                                            onReview={handleReviewChunk}
                                            onEdit={handleEditChunk}
                                            onTranslate={handleTranslateChunk}
                                            onCheckTranslations={handleCheckTranslations}
                                            onMenuOpen={handleMenuOpen}
                                        />
                                    ))}
                                    {provided.placeholder}
                                </List>
                            )}
                        </Droppable>
                    </DragDropContext>
                    
                    {/* Load More Button */}
                    {hasMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={loadMoreChunks}
                                disabled={loadingMore}
                                sx={{ minWidth: 200 }}
                            >
                                {loadingMore ? 'Loading...' : 'Load More Chunks'}
                            </Button>
                        </Box>
                    )}
                </Box>
            ) : (
                <Box textAlign="center" py={8}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No chunks yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Start building your long post by adding the first chunk
                    </Typography>
                </Box>
            )}

            {/* Floating Add Button */}
            <Fab
                color="primary"
                sx={{ position: 'fixed', bottom: 24, right: 24 }}
                onClick={() => setChunkDialog({ 
                    open: true, 
                    chunk: null, 
                    isEdit: false, 
                    isReview: false 
                })}
            >
                <Add />
            </Fab>

            {/* Chunk Editor Component */}
            <LongPostChunkEditor
                postId={id}
                postLanguage={post.language}
                chunkDialog={chunkDialog}
                onClose={handleCloseChunkDialog}
                onSave={refetchData}
                showAlert={showAlert}   
                onEditChunk={handleEditChunk}
            />

            {/* Translation Monitoring Dialog */}
            <TranslationMonitoringDialog
                open={monitoringDialog.open}
                onClose={handleCloseMonitoringDialog}
                loading={monitoringDialog.loading}
                data={monitoringDialog.data}
                onFixInEditor={handleFixInEditor}
            />

            {/* Menu for chunk actions */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={handleDeleteChunk} sx={{ color: 'error.main' }}>
                    <Delete sx={{ mr: 1 }} />
                    Delete Chunk
                </MenuItem>
            </Menu>
        </PageContainer>
    );
};

export default LongPostChunkManager; 